import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { logEvent } from 'services/amplitude';
import _ from 'lodash';
import { IRootState } from 'store';
import { IScholarsRequestData, IScholarsState, MapTemplate } from 'types';
import { Scrollbars } from 'react-custom-scrollbars';
import Title from 'components/common/Title';
import ContainerWithShadow from 'components/common/ContainerWithShadow';
import { defaultMapTemplate, defaultMapType } from 'utils/constants/map';
import buttons from 'style/buttons.module.scss';
import shevron from 'style/imgs/icons/shevron.svg';
import { editScholar } from 'actions/scholars';
import lock from 'style/imgs/icons/lock.svg';
import styles from './index.module.scss';
import Imgs from './imgs';

const mapStateToProps = (state: IRootState) => ({
  activeScholarId: state.users.userData?.activeScholarId,
  scholars: state.scholars,
});

interface MapTemplateProps {
  activeScholarId: number | undefined;
  scholars: IScholarsState;
}

const ChooseMapTemplate: React.FC<MapTemplateProps> = ({
  activeScholarId,
  scholars,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [selectedTemplate, setSelectedTemplate] = useState<MapTemplate>(
    scholars.scholar?.settings?.mapTemplate || defaultMapTemplate
  );
  const [activeUnitGrade, setActiveUnitGrade] = useState<string[]>([]);

  const changeMap = useCallback(
    async (mapTemplate: MapTemplate) => {
      if (!activeScholarId) return;

      const currentUnit = _.find(
        scholars.units,
        (e) => e.id === activeScholarId
      );
      if (!currentUnit) return;

      const editData: Partial<IScholarsRequestData> = {
        id: activeScholarId,
        settings: {
          permissionToEditResources:
            currentUnit.settings?.permissionToEditResources || false,
          permissionToEditTimetable:
            currentUnit.settings?.permissionToEditTimetable || false,
          mapTemplate,
          mapType: scholars.scholar?.settings?.mapType || defaultMapType,
        },
      };

      await dispatch(editScholar(activeScholarId, editData));
    },
    [activeScholarId, scholars, dispatch]
  );

  const handleSelectElement = useCallback(
    (template: MapTemplate) => {
      setSelectedTemplate(template);
      changeMap(template);
    },
    [changeMap]
  );

  useEffect(() => {
    const unit = _.find(scholars.units, (e) => e.id === activeScholarId);
    if (unit) {
      setActiveUnitGrade(unit.grades);
    }
  }, [activeScholarId, scholars.units]);

  const setButtonActive = (state: string) => {
    setActiveUnitGrade([state]);
  };

  return (
    <div className={styles.content}>
      <ContainerWithShadow className={styles.maps}>
        <Title className={styles.title}>Choose Map Template</Title>
        <div className={styles.filters}>
          <button
            className={
              activeUnitGrade[0] === 'All'
                ? buttons.buttonOrange
                : buttons.buttonWhite
            }
            type="button"
            onClick={() => setButtonActive('All')}
          >
            All
          </button>
          <button
            className={
              activeUnitGrade[0] === 'Pre K' ||
              activeUnitGrade[0] === '1' ||
              activeUnitGrade[0] === 'K'
                ? buttons.buttonOrange
                : buttons.buttonWhite
            }
            type="button"
            onClick={() => setButtonActive('1')}
          >
            Pre K - 1
          </button>
          <button
            className={
              +activeUnitGrade >= 2 && +activeUnitGrade <= 5
                ? buttons.buttonOrange
                : buttons.buttonWhite
            }
            type="button"
            onClick={() => setButtonActive('4')}
          >
            Grade 2-5
          </button>
          <button
            className={
              +activeUnitGrade >= 6 && +activeUnitGrade <= 8
                ? buttons.buttonOrange
                : buttons.buttonWhite
            }
            type="button"
            onClick={() => setButtonActive('7')}
          >
            Grade 6-8
          </button>
          <button
            className={
              +activeUnitGrade >= 9 && +activeUnitGrade <= 12
                ? buttons.buttonOrange
                : buttons.buttonWhite
            }
            type="button"
            onClick={() => setButtonActive('11')}
          >
            Grade 9-12
          </button>
        </div>
        <div className={styles.contentWrap}>
          <Scrollbars className={styles.contentBar}>
            <div className={styles.mapList}>
              {!_.isEmpty(Imgs) &&
                Imgs.map((img) => (
                  <div
                    role="button"
                    key={img.id}
                    tabIndex={0}
                    onClick={() => handleSelectElement(img.id)}
                    onKeyDown={() => handleSelectElement(img.id)}
                    className={styles.mapListItem}
                    style={
                      img.id === selectedTemplate && !img.locked
                        ? {
                            border: ' 2px solid #10C9BE',
                            boxShadow: '0px 15px 15px #d5d2f6',
                          }
                        : {}
                    }
                  >
                    <img src={img.url} alt="preview" />
                    {img.id === selectedTemplate && !img.locked && (
                      <div className={styles.checked}>
                        <img src={shevron} alt="check" />
                      </div>
                    )}
                    {img.locked && (
                      <div className={styles.checked}>
                        <img src={lock} alt="lock" />
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </Scrollbars>
        </div>
      </ContainerWithShadow>
      <div className={styles.buttons}>
        <button
          onClick={() =>
            history.push({
              pathname: '/onboarding/profile',
            })
          }
          className={buttons.buttonOutline}
          type="button"
        >
          Back
        </button>
        <button
          onClick={() => {
            history.push('/onboarding/choose-map/switch-template');
            logEvent('ChooseMapNext');
          }}
          className={buttons.buttomPrimary}
          type="button"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default connect(mapStateToProps)(ChooseMapTemplate);
